// Lazy variant of DownloadPdfButton — fetches the full session detail on
// click (history list view only has light items), then triggers the PDF
// download via @react-pdf/renderer's pdf() helper.
import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { httpClient } from '../../services/http.client';
import { HISTORY_ENDPOINTS } from '../../services/api.constants';
import { KeycloakService } from '../../services/keycloak.service';
import { TechniqueDefinitionsService } from '../../services/technique-definitions.service';
import { AnalysisReportPDF } from './AnalysisReportPDF';
import type { AnalysisSession } from '../../types/analysis-session';

interface Props {
  sessionId: string;
  /** When true, button shows only the icon (used in tight history rows). */
  compact?: boolean;
}

const Btn = styled.button<{ compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%);
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: ${p => p.compact ? '6px 10px' : '9px 14px'};
  font-size: ${p => p.compact ? '11px' : '13px'};
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: filter 0.15s ease, transform 0.1s ease;

  &:hover { filter: brightness(1.1); }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: 0.7; cursor: wait; }
`;

const Spin = styled(Loader2)`
  animation: spin 1s linear infinite;
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

interface HistoryDetailResponse {
  success: boolean;
  data: AnalysisSession;
}

export const DownloadPdfButtonLazy: React.FC<Props> = ({ sessionId, compact = false }) => {
  const { t, i18n } = useTranslation();
  const lang: 'ro' | 'en' = (i18n.language || '').toLowerCase().startsWith('ro') ? 'ro' : 'en';
  const [busy, setBusy] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const userId = KeycloakService.getUserFromToken()?.sub;
      const params = userId ? `?user_id=${userId}` : '';
      const url = `${HISTORY_ENDPOINTS.DETAIL(sessionId)}${params}`;

      // Fetch session detail + technique definitions in parallel.
      const [res, techDefs] = await Promise.all([
        httpClient.get<HistoryDetailResponse>(url),
        TechniqueDefinitionsService.getDefinitions().catch(() => []),
      ]);

      if (!res?.success || !res.data) {
        throw new Error('No data');
      }

      const blob = await pdf(
        <AnalysisReportPDF session={res.data} language={lang} techDefs={techDefs} />
      ).toBlob();

      const blobUrl = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `didi-report-${sessionId.substring(0, 8)}-${dateStr}.pdf`;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so the download has time to start.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err: any) {
      console.error('[DownloadPdfButtonLazy] failed:', err);
      const msg = err?.message || String(err);
      alert(`${t('analysis.pdf.alertGenerationFailed')}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const labelDownload = t('analysis.pdf.btnCompactLabel');
  const labelGenerating = t('analysis.pdf.btnCompactGenerating');

  return (
    <Btn compact={compact} disabled={busy} onClick={handleClick} type="button" title={t('analysis.pdf.btnTitle')}>
      {busy ? <Spin size={compact ? 12 : 14} /> : <Download size={compact ? 12 : 14} />}
      {busy ? labelGenerating : labelDownload}
    </Btn>
  );
};

export default DownloadPdfButtonLazy;
